import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { HTMLMotionProps } from 'framer-motion';
import { cx } from '../cx';

type Variant = 'default' | 'raised' | 'flat' | 'hero';

export interface SPCardProps extends Omit<HTMLMotionProps<'div'>, 'ref'> {
  variant?: Variant;
  /** Adds hover-lift affordance + pointer cursor. */
  interactive?: boolean;
  /** Fade/rise in on mount. */
  animateIn?: boolean;
  as?: 'div' | 'article' | 'section' | 'li';
}

/**
 * The base surface of the Journey UI. Pure `--sp-*` tokens (see
 * scss/studentJourneyTheme.scss `.sp-card`). Works in light + dark unchanged.
 */
export const SPCard = React.forwardRef<HTMLDivElement, SPCardProps>(
  ({ variant = 'default', interactive, animateIn, as = 'div', className, children, ...rest }, ref) => {
    const reduce = useReducedMotion();
    const MotionTag = motion[as] as typeof motion.div;

    return (
      <MotionTag
        ref={ref}
        className={cx(
          'sp-card',
          variant === 'raised' && 'sp-card--raised',
          variant === 'flat' && 'sp-card--flat',
          variant === 'hero' && 'sp-card--hero',
          interactive && 'sp-card--interactive',
          className,
        )}
        initial={animateIn && !reduce ? { opacity: 0, y: 10 } : false}
        whileInView={animateIn && !reduce ? { opacity: 1, y: 0 } : undefined}
        viewport={animateIn ? { once: true, margin: '-40px' } : undefined}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        {...rest}
      >
        {children}
      </MotionTag>
    );
  },
);
SPCard.displayName = 'SPCard';
